import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Phone, MessageCircle, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { BRAND, NAV_LINKS } from "@/lib/brand";
import { telLink, generalWhatsapp } from "@/lib/whatsapp";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const go = (l) => {
    const [path, hash] = l.to.split("#");
    if (location.pathname !== path) {
      navigate(l.to);
    } else if (hash) {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 border-b transition-[padding,background-color,box-shadow] duration-300"
      style={{
        background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(16px)",
        borderColor: "rgba(8,127,191,0.1)",
        boxShadow: scrolled ? "0 6px 24px rgba(6,59,99,0.08)" : "none",
      }}
    >
      <div className={`container-x flex items-center justify-between transition-all duration-300 ${scrolled ? "py-2" : "py-3.5"}`}>
        <Link to="/" data-testid="header-logo" className="flex items-center">
          <img
            src={BRAND.logo}
            alt="BIOSYN Advanced Aquaculture Solutions logo"
            className={`w-auto transition-all duration-300 ${scrolled ? "h-9 md:h-10" : "h-11 md:h-14"}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => {
            const active = location.pathname === l.to;
            return (
              <button
                key={l.to}
                onClick={() => go(l)}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="relative px-2.5 py-2 text-sm font-semibold rounded-full transition-colors duration-200 whitespace-nowrap"
                style={{ color: active ? "#087FBF" : "#17324D" }}
              >
                {l.label}
                {active && (
                  <span className="absolute left-2.5 right-2.5 -bottom-0.5 h-0.5 rounded-full" style={{ background: "#16B8D4" }} />
                )}
              </button>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/products" data-testid="header-explore-btn" className="btn-outline-blue !px-5 !py-2.5 text-sm">
            Explore Products
          </Link>
          <Link to="/contact" data-testid="header-expert-btn" className="btn-primary !px-5 !py-2.5 text-sm">
            Talk to Technical Expert <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Mobile actions */}
        <div className="flex lg:hidden items-center gap-2">
          <a href={telLink()} data-testid="header-call-mobile" aria-label="Call BIOSYN" className="p-2.5 rounded-full text-white" style={{ background: "#087FBF" }}>
            <Phone className="w-4 h-4" />
          </a>
          <a href={generalWhatsapp()} target="_blank" rel="noopener noreferrer" data-testid="header-wa-mobile" aria-label="WhatsApp BIOSYN" className="p-2.5 rounded-full text-white" style={{ background: "#25D366" }}>
            <MessageCircle className="w-4 h-4" />
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-trigger" aria-label="Open menu" className="p-2.5 rounded-full border" style={{ borderColor: "rgba(8,127,191,0.25)", color: "#063B63" }}>
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86vw] max-w-sm p-0">
              <div className="p-6 border-b" style={{ borderColor: "rgba(8,127,191,0.12)" }}>
                <img src={BRAND.logo} alt="BIOSYN" className="h-11" />
              </div>
              <div className="flex flex-col p-4">
                {NAV_LINKS.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <button
                      onClick={() => go(l)}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                      className="px-4 py-3.5 rounded-xl text-base font-semibold transition-colors text-left"
                      style={{ color: "#17324D" }}
                    >
                      {l.label}
                    </button>
                  </SheetClose>
                ))}
                <div className="mt-4 flex flex-col gap-3">
                  <SheetClose asChild>
                    <Link to="/products" className="btn-outline-blue w-full">Explore Products</Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/contact" className="btn-primary w-full">Talk to Technical Expert</Link>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
